import {
  AudioLines,
  BookOpenText,
  FlaskConical,
  FolderKanban,
  Inbox,
  Lightbulb,
  MessagesSquare,
  Network,
  Search,
  Sparkles,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

/**
 * Every destination in the app, described once.
 *
 * The rail, the routes and the navigator panel are all rendered from this list
 * rather than each screen owning its own copy. UI-3 asks for the navigator panel's
 * contents to be driven by data, and the reason shows up in every later milestone:
 * filling a room means adding counts and rows to a descriptor here, not rebuilding
 * a bespoke sidebar per page.
 *
 * A count or badge appears only where real data is behind it: an invented number is
 * worse than no number — see docs on honest placeholders below.
 */

/** The three pipeline stages, exposed literally as the contextual bar's own first crumb. */
export type Stage = "sources" | "insights" | "world";

export interface NavigatorRow {
  id: string;
  label: string;
  /** Right-aligned tabular count. Undefined until a milestone has real data. */
  count?: number;
  /** A 6 px status dot, given as a CSS colour token reference. */
  dot?: string;
  /** Route this row navigates to, once it means something. */
  to?: string;
  /**
   * A second level nested under this row — e.g. a recording's meetings. Collapsed by
   * default; the panel expands a row on its own when the filter matches one of its
   * children but not the row itself.
   */
  rows?: NavigatorRow[];
}

export interface NavigatorSection {
  id: string;
  label: string;
  rows: NavigatorRow[];
}

export interface NavigatorSpec {
  /** Uppercase panel title — the page name. */
  title: string;
  /** UI-23 — one line under the title, e.g. the World view's own activity status. */
  subtitle?: string;
  /** Optional filter input above the sections. */
  filter?: string;
  sections: NavigatorSection[];
}

export interface Destination {
  id: string;
  path: string;
  label: string;
  icon: LucideIcon;
  /** Which pipeline stage this destination belongs to, if any. */
  stage?: Stage;
  /** A separator is drawn after this destination in the rail. */
  separatorAfter?: boolean;
  /** Whether the rail shows a count badge here once there is one to show. */
  badged?: boolean;
  /** The milestone that fills this room, named honestly in the empty state. */
  milestone: string;
  /** The page's own subtitle, lifted from the design handoff where it exists. */
  summary: string;
  navigator: NavigatorSpec;
}

/**
 * Rail order, amended in M17.1 (UI-3): World view · People · sep · Recordings · sep ·
 * Review queue · Insights · Entities · Initiatives · sep · Assistant · Search · Chat.
 *
 * The world view is the handbook the owner opens and the people in it are who it is about;
 * recordings are one way it grows, and the rail says so. The handoff drew the pipeline first
 * because the pipeline was what existed first. The stage pills keep their pipeline order,
 * because they describe a flow rather than a priority.
 */
export const DESTINATIONS: Destination[] = [
  {
    id: "world-view",
    path: "/world-view",
    label: "World view",
    icon: BookOpenText,
    stage: "world",
    milestone: "M7.2",
    summary: "Everything you currently hold to be true, with the quotes it came from.",
    navigator: {
      title: "World view",
      filter: "Find a subject…",
      sections: [
        {
          id: "subject-type",
          label: "By subject type",
          rows: [
            { id: "all", label: "All pages" },
            { id: "customers", label: "Customers" },
            { id: "projects", label: "Projects" },
            { id: "people", label: "People" },
            { id: "products", label: "Products" },
            { id: "suppliers", label: "Suppliers" },
            { id: "events", label: "Events" },
          ],
        },
        {
          id: "needs-you",
          label: "Needs you",
          rows: [{ id: "contradictions", label: "Contradictions", dot: "var(--destructive)" }],
        },
        { id: "pages", label: "Pages", rows: [] },
      ],
    },
  },
  {
    id: "people",
    path: "/people",
    label: "People",
    icon: UsersRound,
    separatorAfter: true,
    stage: "insights",
    milestone: "M6.2",
    summary: "Everyone the system has heard, and what it knows about them.",
    navigator: {
      title: "People",
      sections: [
        {
          id: "views",
          label: "Views",
          rows: [
            { id: "gallery", label: "Everyone", to: "/people" },
            // WORLD-10 — the org chart, drawn from reports_to and member_of rows.
            { id: "org-chart", label: "Org chart", to: "/people?view=org-chart" },
          ],
        },
        { id: "everyone", label: "Everyone heard", rows: [] },
      ],
    },
  },
  {
    id: "recordings",
    path: "/recordings",
    label: "Recordings",
    icon: AudioLines,
    separatorAfter: true,
    stage: "sources",
    milestone: "M2.3",
    summary: "Capture meetings, calls and voice memos — track them through the pipeline.",
    navigator: {
      title: "Recordings",
      filter: "Search recordings…",
      sections: [{ id: "all", label: "All recordings", rows: [] }],
    },
  },
  {
    id: "review",
    path: "/review",
    label: "Review queue",
    icon: Inbox,
    stage: "insights",
    badged: true,
    milestone: "M5.3",
    summary: "Everything waiting on your judgement, in one queue.",
    navigator: {
      title: "Review queue",
      sections: [
        {
          id: "by-type",
          label: "By type",
          rows: [
            { id: "all", label: "All" },
            { id: "insights", label: "Insights" },
            { id: "corrections", label: "Corrections" },
            { id: "contradictions", label: "Contradictions" },
            { id: "questions", label: "Questions" },
          ],
        },
        { id: "waiting", label: "Waiting on you", rows: [] },
      ],
    },
  },
  {
    id: "insights",
    path: "/insights",
    label: "Insights",
    icon: Lightbulb,
    stage: "insights",
    milestone: "M7.3",
    summary: "Every insight read out of a source, and what it did to your beliefs.",
    navigator: {
      title: "Insights",
      sections: [
        {
          id: "horizon",
          label: "Time horizon",
          rows: [
            { id: "7d", label: "7 days" },
            { id: "30d", label: "30 days" },
            { id: "90d", label: "90 days" },
            { id: "all", label: "All time" },
          ],
        },
        {
          id: "status",
          label: "By status",
          rows: [
            { id: "all", label: "All" },
            { id: "approved", label: "Approved" },
            { id: "pending", label: "Pending review" },
            { id: "discarded", label: "Discarded" },
          ],
        },
        {
          id: "verdict",
          label: "By effect on beliefs",
          rows: [
            { id: "any", label: "Any effect" },
            { id: "open", label: "Open", dot: "var(--vl-bridge)" },
            { id: "extend", label: "Extend", dot: "var(--vl-virtual)" },
            { id: "corroborate", label: "Corroborate", dot: "var(--success)" },
            { id: "weaken", label: "Weaken", dot: "var(--warning)" },
            { id: "supersede", label: "Supersede", dot: "var(--vl-accent-warm)" },
            { id: "challenge", label: "Challenge", dot: "var(--destructive)" },
          ],
        },
        { id: "source", label: "By source", rows: [] },
      ],
    },
  },
  {
    id: "entities",
    path: "/entities",
    label: "Entities",
    icon: Network,
    stage: "insights",
    milestone: "M4.1",
    summary: "The people, products, customers and projects your sources talk about.",
    navigator: {
      title: "Entities",
      sections: [
        {
          id: "by-type",
          label: "By type",
          rows: [
            { id: "all", label: "All" },
            { id: "persons", label: "Persons" },
            { id: "products", label: "Products" },
            { id: "services", label: "Services" },
            { id: "customers", label: "Customers" },
            { id: "suppliers", label: "Suppliers" },
            { id: "locations", label: "Locations" },
            { id: "events", label: "Events" },
            { id: "projects", label: "Projects" },
            { id: "teams", label: "Teams" },
          ],
        },
        { id: "in-view", label: "In this view", rows: [] },
      ],
    },
  },
  {
    id: "initiatives",
    path: "/initiatives",
    label: "Initiatives",
    icon: FolderKanban,
    separatorAfter: true,
    stage: "insights",
    milestone: "M16.5",
    summary: "The projects and pushes you're running, each with a main goal and its own dossier.",
    navigator: {
      title: "Initiatives",
      sections: [{ id: "all", label: "All initiatives", rows: [] }],
    },
  },
  {
    id: "assistant",
    path: "/assistant",
    label: "Assistant",
    icon: Sparkles,
    // AUTO-4 fills the Open loops tab in M10.3; Recaps and Tomorrow arrive with M10.2 and
    // Suggestions with M10.4, where UI-15 is listed as finished.
    milestone: "M10.3",
    summary: "Recaps, open loops and what tomorrow needs from you.",
    navigator: {
      title: "Assistant",
      sections: [
        {
          id: "assistant",
          label: "Assistant",
          rows: [
            { id: "recaps", label: "Recaps" },
            { id: "open-loops", label: "Open loops", to: "/assistant" },
            { id: "tomorrow", label: "Tomorrow" },
            { id: "suggestions", label: "Suggestions" },
          ],
        },
        {
          id: "needs-you",
          label: "Needs you",
          rows: [
            { id: "review", label: "Review queue", to: "/review" },
            { id: "contradictions", label: "Contradictions", to: "/world-view" },
          ],
        },
      ],
    },
  },
  {
    id: "search",
    path: "/search",
    label: "Search",
    icon: Search,
    // SRCH-1 fills this room in M6.1; the filters and the altitude toggle the handoff
    // draws above the results arrive with SRCH-2/SRCH-3 in M8.1.
    milestone: "M6.1",
    summary: "Search insights and your world view, always with provenance.",
    navigator: {
      title: "Search",
      sections: [{ id: "recent", label: "Recent searches", rows: [] }],
    },
  },
  {
    id: "chat",
    path: "/chat",
    label: "Chat",
    icon: MessagesSquare,
    // CHAT-2, and M9.2 rather than M9.1 — that increment built the MCP server, which has no
    // screen at all. Corrected in M10.3 rather than left to mislead somebody reading the
    // empty state.
    milestone: "M9.2",
    summary: "Ask anything. Every answer cites the sources it came from.",
    navigator: {
      title: "Chat",
      sections: [{ id: "conversations", label: "Conversations", rows: [] }],
    },
  },
];

export const DESTINATIONS_BY_ID = new Map(DESTINATIONS.map((d) => [d.id, d]));

/**
 * UI-24 — "Insight processing", the developer-only view of WORLD-2: what a synthesis batch
 * read, what the model said, what the parser and the database kept, and why. A separate list
 * rather than a row in {@link DESTINATIONS}, appended to the rail only while developer mode is
 * on (`DestinationRail`) — keeping it out of the always-on list is what lets the cold-load and
 * rail-overflow tests keep describing the ordinary rail without filtering a developer entry
 * out of every assertion that enumerates it.
 *
 * `AppShell`'s navigator for this destination is built from live batches
 * (`processingNavigator`), so the static spec below is only the address's own fallback —
 * never drawn once a batch has loaded, per the pattern every dynamic destination follows.
 */
export const DEVELOPER_DESTINATIONS: Destination[] = [
  {
    id: "processing",
    path: "/processing",
    label: "Insight processing",
    icon: FlaskConical,
    stage: "world",
    milestone: "M21",
    summary: "What a synthesis batch read, what the model said, and what became a belief.",
    navigator: {
      title: "Insight processing",
      sections: [{ id: "batches", label: "Batches", rows: [] }],
    },
  },
];

export const DEVELOPER_DESTINATIONS_BY_ID = new Map(DEVELOPER_DESTINATIONS.map((d) => [d.id, d]));

/**
 * UI-16 — the rail simple mode rewrites, in the handoff's order:
 * World view · Needs you · sep · Ask · Search.
 *
 * Four of the ten destinations, named from the same list rather than a parallel one, so a
 * simple-mode button cannot lead to an address the router does not answer. Two of them are
 * renamed rather than reworded in place: the Review queue is **Needs you** here and Chat is
 * **Ask**, because simple mode's copy has to carry the meaning without the machinery's
 * vocabulary — but they are the same routes, so a link, a citation and a deep link mean
 * exactly one thing in either mode.
 *
 * What is missing is the point: Recordings, Insights, Entities, People and Assistant are
 * every screen that shows the pipeline working, and a surface for somebody who should never
 * have to know what a diarizer is does not lead there.
 */
export interface SimpleRailEntry {
  /** A {@link Destination} id — the route and the icon come from that descriptor. */
  id: string;
  /** What this destination is called in simple mode, where it differs. */
  label: string;
  separatorAfter?: boolean;
}

export const SIMPLE_RAIL: SimpleRailEntry[] = [
  { id: "world-view", label: "World view" },
  { id: "review", label: "Needs you", separatorAfter: true },
  { id: "chat", label: "Ask" },
  { id: "search", label: "Search" },
];

/** What a destination is called in simple mode, for the breadcrumb and the page heading. */
export const SIMPLE_LABELS: Record<string, string> = Object.fromEntries(
  SIMPLE_RAIL.map((entry) => [entry.id, entry.label]),
);

/** Where a stage's own link in the breadcrumb trail navigates, per the handoff. */
export const STAGE_HOME: Record<Stage, string> = {
  sources: "/recordings",
  insights: "/insights",
  world: "/world-view",
};

/** The stage's own name, as the first link in the contextual bar's trail. */
export const STAGE_LABEL: Record<Stage, string> = {
  sources: "Sources",
  insights: "Insights",
  world: "World view",
};
